import gql from 'graphql-tag'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'
import OrderProductModule from './product'
import OrderPersonalDataModule from './personalData'
import OrderTopUpModule from './topUp'
import OrderPaymentModule from './payment'
import OrderMnpModule from './mnp'
import OrderReferFriendModule from './referFriend'
import GtmEventDataModule from './gtmEventData'

const initialState = {
  step: 'shoppingCard',
  processId: null,
  paypalPayOnToken: null,
  paypalFinished: false,
  confirmResponse: null,
  customer: null,
  orderContext: {
    market: 'PUA'
  },
  consents: {},
  options: false,
  orderConsent: {
    adConsent: false,
    passData: false,
    agb: true,
    contractTermsVersion: '1.0'
  },
  contractDocumentUrl: null,
  contractId: null
}
const OrderModule = {
  namespaced: true,
  state: initialState,
  modules: {
    product: OrderProductModule,
    personalData: OrderPersonalDataModule,
    topUp: OrderTopUpModule,
    mnp: OrderMnpModule,
    payment: OrderPaymentModule,
    referFriend: OrderReferFriendModule,
    gtmEventData: GtmEventDataModule
  },
  actions: {
    async confirmOrder ({ commit, state, getters, rootGetters, dispatch }, { step, consents }) {
      commit('clearMessages', {}, { root: true })
      // if we are called without consents we use the ones defined previously before paypal step
      if (!consents && getters.requiresPaypalFlow) {
        consents = getters.consents
      }
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation order ($orderConfirmInput: OrderConfirmInput!) {
          orderConfirm (orderConfirm: $orderConfirmInput){
            contractId
            customerId
            deferredIdentificationResponse {
              actionID
              identProvider
              url
            }
          }
        }`,
        variables: {
          orderConfirmInput: {
            brand: rootGetters.brand,
            processId: state.processId,
            orderConsent: {
              adConsent: consents.contact,
              passData: consents.exchange,
              agb: state.orderConsent.agb,
              contractTermsVersion: state.orderConsent.contractTermsVersion
            },
            payOnPaypalToken: getters.paypalPayOnToken
          }
        }
      }).then((response) => {
        commit('storeConsents', consents)
        commit('changeStep', { step })
        commit('confirmResponse', response.data.orderConfirm)
        commit('storeContractId', response.data.orderConfirm.contractId)
      }).catch((error) => {
        let paypalPaymentError = false
        splitError(error).forEach(e => {
          commit('addError', e, { root: true })
          const details = e.extensions
          if (getters.requiresPaypalFlow) {
            if (details?.process === 'payment') {
              // error was during payment process, we need to reinitialize
              paypalPaymentError = true
            } else {
              // another error occurred, since payment validation is done first, we can assume payment was successful
              // and therefore must not do the paypal flow again
              commit('paypalFinished', true)
            }
          }
        })
        if (paypalPaymentError) {
          /*
           * if confirm was unsuccessful because of payment, reinitialize to get a new payment token
           */
          return dispatch('initiateOrder', { step: 'summary', reInitAfterPaymentError: true })
        }
      })
    },
    async initiateOrder ({ commit, state, getters, rootGetters }, { step, reInitAfterPaymentError = false }) {
      const apolloClient = getProvider().defaultClient
      if (reInitAfterPaymentError === false) {
        commit('clearMessages', {}, { root: true })
      }
      return apolloClient.mutate({
        mutation: gql`mutation order ($orderInitiateInput: OrderInitiateInput!){
          orderInitiate (orderInitiate: $orderInitiateInput) {
            documentUrl
            processId
            token
          }
        }`,
        variables: {
          orderInitiateInput: {
            brand: rootGetters.brand,
            customer: rootGetters['orderProduct/personalData/customerCore'],
            deferredIdentification: rootGetters['orderProduct/personalData/identificationCore'],
            payment: rootGetters['orderProduct/payment/getPayment'],
            orderContext: state.orderContext,
            product: rootGetters['orderProduct/product/productCore'],
            topup: rootGetters['orderProduct/topUp/topUpCore'],
            mobileNumberPortability: rootGetters['orderProduct/mnp/mnpCore'],
            referFriend: rootGetters['friendNotification/referFriend']
          }
        }
      }).then((response) => {
        commit('storeContractDocumentUrl', response.data.orderInitiate.documentUrl + '?contentDisposition=inline')
        commit('processId', response.data.orderInitiate.processId)
        commit('storePaypalPayOnToken', response.data.orderInitiate.token)
        commit('changeStep', { step })
        return response.data
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    }
  },
  mutations: {
    processId (state, id) {
      state.processId = id
    },
    storePaypalPayOnToken (state, token) {
      state.paypalPayOnToken = token
      state.paypalFinished = false
    },
    commitCustomer (state, { customer }) {
      state.customer = customer
    },
    commitOptions (state, { options }) {
      state.options = options
    },
    changeStep (state, { step }) {
      state.step = step
    },
    confirmResponse (state, confirmResponse) {
      state.confirmResponse = confirmResponse
    },
    storeContractDocumentUrl: (state, contractDocumentUrl) => {
      state.contractDocumentUrl = contractDocumentUrl
    },
    storeConsents: (state, consents) => {
      state.consents = consents
    },
    paypalFinished: (state, finished) => {
      state.paypalFinished = finished
    },
    storeContractId: (state, contractId) => {
      state.contractId = contractId
    }
  },
  getters: {
    getStep: state => {
      return state.step
    },
    getProcessId: state => {
      return state.processId
    },
    getConfirmResponse: state => {
      return state.confirmResponse
    },
    getCustomer: state => {
      return state.customer
    },
    getOptions: state => {
      return state.options
    },
    getMarket: state => {
      return 'PUA'
    },
    getIdentMethod: state => {
      if (state.confirmResponse.deferredIdentificationResponse.identProvider === 'Deutsche_Post') {
        if (state.personalData.identInformation.identType === 'video') {
          return 'videoIdentPost'
        }
        if (state.personalData.identInformation.identType === 'store') {
          return 'postIdent'
        }
      }
      if (state.confirmResponse.deferredIdentificationResponse.identProvider === 'WebId') {
        return 'videoIdentWeb'
      }
      return null
    },
    contractDocumentUrl: (state) => {
      return state.contractDocumentUrl
    },
    consents: (state) => {
      return state.consents
    },
    paypalPayOnToken: (state) => state.paypalPayOnToken,
    requiresPaypalFlow: (state, getters, rootState, rootGetters) => {
      return rootGetters['orderProduct/payment/getPayment']?.paymentType === 'paypal' && getters.paypalPayOnToken !== null
    },
    paypalFinished: (state) => state.paypalFinished,
    getContractId: (state) => state.contractId
  }
}
export {
  OrderModule
}
